
/**
 * Utility functions for the application
 */
import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names using clsx and tailwind-merge
 * This utility is used throughout the UI components
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Function to calculate the time remaining until a token expires, with a buffer
 * @param token The JWT token
 * @param bufferMinutes The buffer time in minutes
 * @returns The time remaining in seconds, or null if an error occurs
 */
export const getTokenExpiryWithBuffer = (token: string, bufferMinutes = 5): number | null => {
  try {
    if (!token) {
      console.warn("No token provided to getTokenExpiryWithBuffer");
      return null;
    }

    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) {
      console.warn("Invalid token format: missing payload");
      return null;
    }

    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

    if (!payload || !payload.exp) {
      console.warn("Token payload missing 'exp' claim");
      return null;
    }

    const expiryTimeSeconds = payload.exp as number;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const bufferSeconds = bufferMinutes * 60;

    return (expiryTimeSeconds - nowSeconds) - bufferSeconds;
  } catch (error) {
    console.error("Error decoding or processing token:", error);
    return null;
  }
};

/**
 * Check if code is running in a browser environment
 * @deprecated Use isBrowser from src/lib/utils/environment.ts instead
 */
export const isBrowser = (): boolean => {
  return typeof window !== 'undefined';
};

/**
 * Debounce function to limit the rate at which a function is called
 * @param func The function to debounce
 * @param delay The delay in milliseconds
 * @returns A debounced function
 */
export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function debouncedFunction(this: any, ...args: any[]) {
    const context = this;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  } as T;
}
